import { useCallback } from "react";
import type { NativeSyntheticEvent, ViewProps } from "react-native";
import Animated from "react-native-reanimated";

import type { PressableProps } from "./types";

const Pressable = ({
  ref,
  children,
  style,
  onPress,
  disabled,
  onFocus,
  onBlur,
  onLayout,
  accessibilityRole = "button",
  focusable,
  ...restProps
}: PressableProps) => {
  const handlePress = useCallback(() => {
    if (disabled) return;
    onPress?.();
  }, [disabled, onPress]);

  const handleKeyDown = useCallback(
    (event: NativeSyntheticEvent<{ key: string }>) => {
      if (disabled) return;

      const { key } = event.nativeEvent;
      if (key === "Enter" || key === " ") {
        event.preventDefault();
        onPress?.();
      }
    },
    [disabled, onPress],
  );

  const eventHandlers = {
    onClick: disabled ? undefined : handlePress,
    onKeyDown: handleKeyDown,
  } as ViewProps;

  return (
    <Animated.View
      ref={ref}
      accessibilityRole={accessibilityRole}
      aria-disabled={disabled}
      focusable={focusable ?? true}
      tabIndex={disabled || focusable === false ? -1 : 0}
      onLayout={onLayout}
      onFocus={onFocus}
      onBlur={onBlur}
      style={[{ cursor: disabled ? "auto" : "pointer" }, style]}
      {...eventHandlers}
      {...restProps}
    >
      {children}
    </Animated.View>
  );
};

export default Pressable;
